import { type CustomerOrderDetail, formatSizeBreakdown } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount, formatDateTime, formatKrw } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { OrderShipmentList } from './order-shipment-list';

interface OrderDestinationCardProps {
  /** 1부터 — 카드 이름 "배송지 N" 이다. */
  readonly order: number;
  readonly destination: CustomerOrderDetail['destinations'][number];
  /** 발행본의 이 배송지 배송비 줄 금액 — 짝이 없으면 null(칸을 만들지 않는다). */
  readonly fee: number | null;
  /** 결제 완료·배송 완료 전이면 송장이 없을 때 '아직 출고 전'을 적는다. */
  readonly awaitingShipment: boolean;
  /** 이 배송지 주소를 지금 바꿀 수 있는지(`canChangeDestinationAddress`). */
  readonly editable: boolean;
  readonly pending: boolean;
  readonly onChangeAddress: () => void;
}

const ROW_CLASS = 'flex gap-4 py-3';
const TERM_CLASS = 'w-24 shrink-0 text-muted-foreground sm:w-28';

/**
 * 주문 배송지 카드(SUPER-86 spec §5-4·§5-5) — 결제 정보와 같은 이름/값 줄(수령인·주소·수량·배송비·변경 일시) 아래에 그 배송지의 송장을
 * 둔다. 송장 없는 배송지만 [배송지 변경]이 있다(배송 중이어도).
 */
export const OrderDestinationCard = ({
  order,
  destination,
  fee,
  awaitingShipment,
  editable,
  pending,
  onChangeAddress,
}: OrderDestinationCardProps): ReactElement => {
  const address = destination.shippingAddress;
  return (
    <section aria-label={`배송지 ${order}`} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">배송지 {order}</h3>
      <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm wrap-anywhere">
        <div className={ROW_CLASS}>
          <dt className={TERM_CLASS}>수령인</dt>
          <dd className="min-w-0 flex-1">
            {address.recipientName} · {address.recipientPhone}
          </dd>
        </div>
        <div className={ROW_CLASS}>
          <dt className={TERM_CLASS}>주소</dt>
          <dd className="min-w-0 flex-1">
            ({address.postalCode}) {address.address1} {address.address2}
          </dd>
        </div>
        <div className={ROW_CLASS}>
          <dt className={TERM_CLASS}>수량</dt>
          <dd className="tabular-nums">
            {formatCount(destination.quantity)}개
            {destination.sizes.length > 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {formatSizeBreakdown(destination.sizes)}
              </p>
            ) : null}
          </dd>
        </div>
        {fee !== null ? (
          <div className={ROW_CLASS}>
            <dt className={TERM_CLASS}>배송비</dt>
            <dd className="tabular-nums">{formatKrw(fee)}</dd>
          </div>
        ) : null}
        {destination.addressUpdatedAt !== null ? (
          <div className={ROW_CLASS}>
            <dt className={TERM_CLASS}>변경 일시</dt>
            <dd className="tabular-nums">{formatDateTime(destination.addressUpdatedAt)}</dd>
          </div>
        ) : null}
      </dl>
      {destination.shipments.length > 0 ? (
        <OrderShipmentList
          shipments={destination.shipments}
          heading={null}
          label={`배송지 ${order} 송장`}
        />
      ) : awaitingShipment ? (
        <p className="text-sm text-muted-foreground">아직 출고 전이에요</p>
      ) : null}
      {editable ? (
        <Button
          type="button"
          variant="outline"
          className="h-11 w-fit border-foreground/20 px-5 text-sm"
          aria-label={`배송지 ${order} 변경`}
          disabled={pending}
          onClick={onChangeAddress}
        >
          배송지 변경
        </Button>
      ) : null}
    </section>
  );
};
